import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ModelType, IVideoLipsync, PaginationResult, VideoLipsyncQuery, PaginationQuery, VideoLipsyncStatus } from '@packages/global';
import { videoLipsyncApi } from '../services/user';
import { useModels } from '../hooks/useModels';
import Pagination from '../components/Pagination';
import VideoLipsyncCard from '../components/VideoLipsync/VideoLipsyncCard';
import SearchBar from '../components/VideoLipsync/SearchBar';
import VideoLipsyncForm from '../components/VideoLipsync/VideoLipsyncForm';
import { useDialog } from '../hooks/useDialog';
import { useWebSocketContext } from '../services/websocket/WebSocketContext';
import { MessageType, TaskStatusChangedMessage, TaskType, TaskStatus } from '../services/websocket/types';
import '../styles/line-clamp.css';

interface VideoLipsyncFormData {
  videoUrl: string;
  audioUrl: string;
  modelId: string;
}

export default function VideoLipsyncsPage() {
  const { t } = useTranslation(['generation', 'common']);
  const [items, setItems] = useState<IVideoLipsync[]>([]);
  const { models, loading: modelsLoading } = useModels(ModelType.VIDEO_LIPSYNC);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState<IVideoLipsync | null>(null);
  const { alert, confirm } = useDialog();
  const { client, isConnected } = useWebSocketContext();
  const [formData, setFormData] = useState<VideoLipsyncFormData>({
    videoUrl: '',
    audioUrl: '',
    modelId: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<VideoLipsyncStatus | ''>('');
  const [pagination, setPagination] = useState<PaginationResult>({
    page: 1,
    limit: 24,
    total: 0,
    totalPages: 0,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  const loadItems = async (
    queryParams: Partial<VideoLipsyncQuery> = {},
    page: number = 1,
    limit: number = 24
  ) => {
    try {
      const queryData: PaginationQuery<VideoLipsyncQuery> = {
        query: {
          search: queryParams.search ?? searchTerm,
          status: queryParams.status ?? (statusFilter || undefined),
          ...queryParams
        },
        pagination: {
          page,
          limit,
          sortBy: 'createdAt',
          sortOrder: 'desc'
        }
      };
      const response = await videoLipsyncApi.videoLipsyncPageList(queryData);
      setItems(response.items);
      setPagination((prev) => ({
        ...prev,
        page,
        limit,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages
      }));
    } catch (error) {
      console.error('加载视频配音任务失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  // 🔥 页面级别订阅 WebSocket，监听所有视频配音任务的状态更新
  useEffect(() => {
    if (!client || !isConnected) {
      return;
    }

    // 监听状态变化消息
    const unsubscribeStatusChanged = client.onMessage(
      MessageType.TASK_STATUS_CHANGED,
      (message: TaskStatusChangedMessage) => {
        if (message.taskType !== TaskType.VIDEO_LIPSYNC) return;

        const statusMap: Record<TaskStatus, VideoLipsyncStatus> = {
          [TaskStatus.PENDING]: VideoLipsyncStatus.PENDING,
          [TaskStatus.SUBMITTED]: VideoLipsyncStatus.SUBMITTED,
          [TaskStatus.PROCESSING]: VideoLipsyncStatus.PROCESSING,
          [TaskStatus.COMPLETED]: VideoLipsyncStatus.COMPLETED,
          [TaskStatus.FAILED]: VideoLipsyncStatus.FAILED
        };

        const newStatus = statusMap[message.newStatus];
        if (!newStatus) return;

        setItems(prevItems => {
          const itemIndex = prevItems.findIndex(item => item.id === message.taskId);
          if (itemIndex === -1) return prevItems;

          const newItems = [...prevItems];
          newItems[itemIndex] = { ...newItems[itemIndex], status: newStatus };

          if (newStatus === VideoLipsyncStatus.COMPLETED || newStatus === VideoLipsyncStatus.FAILED) {
            videoLipsyncApi.getVideoLipsync(message.taskId)
              .then(updatedItem => {
                setItems(prev => prev.map(item => item.id === updatedItem.id ? updatedItem : item));
              })
              .catch(console.error);
          }

          return newItems;
        });
      }
    );

    const unsubscribeCompleted = client.onMessage(MessageType.TASK_COMPLETED, (message: any) => {
      if (message.taskType !== TaskType.VIDEO_LIPSYNC) return;
      videoLipsyncApi.getVideoLipsync(message.taskId)
        .then(updatedItem => setItems(prev => prev.map(item => item.id === updatedItem.id ? updatedItem : item)))
        .catch(console.error);
    });

    const unsubscribeFailed = client.onMessage(MessageType.TASK_FAILED, (message: any) => {
      if (message.taskType !== TaskType.VIDEO_LIPSYNC) return;
      videoLipsyncApi.getVideoLipsync(message.taskId)
        .then(updatedItem => setItems(prev => prev.map(item => item.id === updatedItem.id ? updatedItem : item)))
        .catch(console.error);
    });

    return () => {
      unsubscribeStatusChanged();
      unsubscribeCompleted();
      unsubscribeFailed();
    };
  }, [client, isConnected]);

  // 当模型加载完成且表单中没有选择模型时，自动选择第一个模型
  useEffect(() => {
    if (!modelsLoading && !formData.modelId && models.length > 0) {
      setFormData(prev => ({ ...prev, modelId: models[0].id }));
    }
  }, [models, modelsLoading, formData.modelId]);

  const handleSearchTermChange = (term: string) => {
    setSearchTerm(term);
    loadItems({ search: term, status: statusFilter || undefined }, 1);
  };

  const handleStatusFilterChange = (status: VideoLipsyncStatus | '') => {
    setStatusFilter(status);
    loadItems({ search: searchTerm, status: status || undefined }, 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.modelId) {
      await alert(t('generation:lipsync.alertSelectModel'), t('generation:lipsync.alertHint'), 'warning');
      return;
    }

    if (!formData.videoUrl) {
      await alert(t('generation:lipsync.alertUploadVideo'), t('generation:lipsync.alertHint'), 'warning');
      return;
    }

    if (!formData.audioUrl) {
      await alert(t('generation:lipsync.alertUploadAudio'), t('generation:lipsync.alertHint'), 'warning');
      return;
    }

    try {
      const newTask = await videoLipsyncApi.createVideoLipsync(formData);
      // 🔥 直接把新任务加入列表头部
      setItems(prevItems => [newTask, ...prevItems]);
      setPagination(prev => ({ ...prev, total: prev.total + 1 }));
      handleCloseForm();
    } catch (error) {
      console.error('操作失败:', error);
    }
  };

  const handleEdit = (item: IVideoLipsync) => {
    setEditingItem(item);
    setFormData({
      videoUrl: item.videoUrl,
      audioUrl: item.audioUrl,
      modelId: item.modelId
    });
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm(t('generation:lipsync.deleteConfirm'), t('common:confirmDelete'), 'danger');
    if (confirmed) {
      try {
        await videoLipsyncApi.deleteVideoLipsync(id);
        loadItems();
      } catch (error) {
        console.error('删除失败:', error);
      }
    }
  };

  const handleCloseForm = () => {
    setEditingItem(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      videoUrl: '',
      audioUrl: '',
      modelId: models.length > 0 ? models[0].id : ''
    });
  };

  const handleFormDataChange = (data: Partial<VideoLipsyncFormData>) => {
    setFormData(prev => ({ ...prev, ...data }));
  };

  return (
    <div className="flex h-screen bg-bg-page">
      {/* 左侧表单 */}
      <VideoLipsyncForm
        editingItem={editingItem}
        formData={formData}
        models={models}
        modelsLoading={modelsLoading}
        onSubmit={handleSubmit}
        onFormDataChange={handleFormDataChange}
        onReset={handleCloseForm}
      />

      {/* 右侧内容区域 */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          {/* 页面标题 */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-50">{t('generation:lipsync.title')}</h1>
              <p className="mt-1 text-sm text-gray-400">{t('generation:lipsync.subtitle')}</p>
            </div>
            <SearchBar
              searchTerm={searchTerm}
              statusFilter={statusFilter}
              onSearchTermChange={handleSearchTermChange}
              onStatusFilterChange={handleStatusFilterChange}
            />
          </div>

          {/* 卡片网格 */}
          {loading ? (
            <div className="bg-bg-surface rounded-xl border border-border-subtle shadow-sm p-12">
              <div className="flex justify-center items-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="ml-3 text-gray-400">{t('common:loading')}</span>
              </div>
            </div>
          ) : items.length === 0 ? (
            <div className="bg-bg-surface rounded-xl border border-border-subtle shadow-sm p-12">
              <div className="text-center">
                <p className="text-gray-400">{t('generation:lipsync.empty')}</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,240px)] justify-between gap-x-4 gap-y-6">
              {items.map((item) => (
                <VideoLipsyncCard
                  key={item.id}
                  item={item}
                  models={models}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}

          {/* 分页组件 */}
          {pagination.totalPages > 1 && (
            <div className="bg-bg-surface rounded-xl border border-border-subtle shadow-sm p-4 mt-6">
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={(page) => loadItems({}, page, pagination.limit)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}